import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
export type BubbleColorScheme = 'default' | 'indigo' | 'amber' | 'emerald' | 'violet';
const COLOR_SCHEMES: Record<BubbleColorScheme, {
  bg: string;
  text: string;
  border: string;
  action: string;
  pointer: string;
}> = {
  default: {
    bg: 'bg-white',
    text: 'text-slate-700',
    border: 'border-slate-200',
    action: 'text-indigo-600 hover:text-indigo-700',
    pointer: 'bg-white border-slate-200'
  },
  indigo: {
    bg: 'bg-indigo-50',
    text: 'text-indigo-900',
    border: 'border-indigo-200',
    action: 'text-indigo-700 hover:text-indigo-800',
    pointer: 'bg-indigo-50 border-indigo-200'
  },
  amber: {
    bg: 'bg-amber-50',
    text: 'text-amber-900',
    border: 'border-amber-200',
    action: 'text-amber-700 hover:text-amber-800',
    pointer: 'bg-amber-50 border-amber-200'
  },
  emerald: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-900',
    border: 'border-emerald-200',
    action: 'text-emerald-700 hover:text-emerald-800',
    pointer: 'bg-emerald-50 border-emerald-200'
  },
  violet: {
    bg: 'bg-violet-50',
    text: 'text-violet-900',
    border: 'border-violet-200',
    action: 'text-violet-700 hover:text-violet-800',
    pointer: 'bg-violet-50 border-violet-200'
  }
};
export interface DjayBubbleProps {
  message: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  autoDismissMs?: number;
  position?: 'top' | 'bottom' | 'left' | 'right';
  colorScheme?: BubbleColorScheme;
  className?: string;
  onDismiss?: () => void;
  isVisible?: boolean;
}
/**
 * DjayBubble — Chat-style speech bubble that pairs with DjayAvatar.
 * DDD co-locatie: moved from components/mascot/ in s60.
 */
export function DjayBubble({
  message,
  action,
  autoDismissMs = 8000,
  position = 'right',
  colorScheme = 'default',
  className = '',
  onDismiss,
  isVisible = true
}: DjayBubbleProps) {
  const [visible, setVisible] = useState(isVisible);
  const colors = COLOR_SCHEMES[colorScheme];
  useEffect(() => {
    setVisible(isVisible);
  }, [isVisible]);
  useEffect(() => {
    if (!visible || autoDismissMs <= 0) return;
    const timer = setTimeout(() => {
      setVisible(false);
      onDismiss?.();
    }, autoDismissMs);
    return () => clearTimeout(timer);
  }, [visible, autoDismissMs, onDismiss]);
  const pointerStyles: Record<string, string> = {
    top: 'bottom-full left-4 border-l-transparent border-r-transparent border-t-transparent border-b-white mb-[-1px]',
    bottom: 'top-full left-4 border-l-transparent border-r-transparent border-b-transparent border-t-white mt-[-1px]',
    left: 'right-full top-3 border-t-transparent border-b-transparent border-l-transparent border-r-white mr-[-1px]',
    right: 'left-full top-3 border-t-transparent border-b-transparent border-r-transparent border-l-white ml-[-1px]'
  };
  const pointerBorderStyles: Record<string, string> = {
    top: 'border-b-[10px] border-l-[8px] border-r-[8px]',
    bottom: 'border-t-[10px] border-l-[8px] border-r-[8px]',
    left: 'border-r-[10px] border-t-[8px] border-b-[8px]',
    right: 'border-l-[10px] border-t-[8px] border-b-[8px]'
  };
  const bubbleVariants = {
    hidden: {
      opacity: 0,
      scale: 0.85,
      y: position === 'top' ? 10 : position === 'bottom' ? -10 : 0,
      x: position === 'left' ? 10 : position === 'right' ? -10 : 0
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      x: 0,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 25
      }
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      transition: {
        duration: 0.15,
        ease: 'easeOut'
      }
    }
  };
  return <AnimatePresence data-id="element-2753">
      {visible && <motion.div variants={bubbleVariants} initial="hidden" animate="visible" exit="exit" className={`relative inline-block ${className}`} data-id="element-2754">
          <div className={`${colors.bg} rounded-2xl shadow-lg border ${colors.border} px-4 py-3 max-w-[280px]`} data-id="element-2755">
            <p className={`${colors.text} font-medium text-sm leading-relaxed`} data-id="element-2756">
              {message}
            </p>
            {action && <button onClick={action.onClick} className={`mt-2 ${colors.action} font-bold text-sm transition-colors inline-flex items-center gap-1`} data-id="element-2757">
                {action.label}
                <span aria-hidden="true" data-id="element-2758">→</span>
              </button>}
          </div>
          <div className={`absolute w-0 h-0 ${pointerStyles[position]} ${pointerBorderStyles[position]}`} aria-hidden="true" data-id="element-2759" />
        </motion.div>}
    </AnimatePresence>;
}